import { useRef } from 'react';
import { Modal } from '@/components/ui/Modal';
import { VideoEmbed } from '@/components/ui/VideoEmbed';
import { hasExtraDetails, richTextToPlain } from '@/lib/alertHeroStyle';
import type { AccountAlert } from '@/services/accountAlerts.service';
import { AlertHeroCard } from './AlertHeroCard';

interface AlertHeroCardModalProps {
  alert: AccountAlert | null;
  dateLabel?: string | null;
  /** Status pill for the announcements archive, drawn in the card's own colors. */
  statusLabel?: string;
  onClose: () => void;
}

/**
 * One announcement at full size: the same card the dashboard banner shows, with
 * its video and "More Details" copy laid out beneath it.
 *
 * On the banner, the card's "Watch video" and "More Details" links open
 * AlertDetailModal. Here they scroll down to that content instead, because the
 * shared Modal does not stack: every open one listens for Escape, so a second
 * modal on top of this one would close both at once.
 *
 * The card gets no close/restore control. It would sit directly under the
 * modal's own X, one click from closing the announcement instead of the modal;
 * the archive tile keeps that control.
 */
export function AlertHeroCardModal({ alert, dateLabel, statusLabel, onClose }: AlertHeroCardModalProps) {
  const extrasRef = useRef<HTMLDivElement>(null);
  if (!alert) return null;

  const showDetails = hasExtraDetails(alert.alertContent, alert.moreDetails);

  return (
    <Modal isOpen onClose={onClose} title="Announcement" size="xl">
      <AlertHeroCard
        alert={alert}
        dateLabel={dateLabel}
        statusLabel={statusLabel}
        onOpenDetail={() => extrasRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
      />

      {(alert.videoURL || showDetails) && (
        <div ref={extrasRef} className="mt-5 space-y-5">
          {alert.videoURL && (
            <VideoEmbed url={alert.videoURL} title={richTextToPlain(alert.alertTitle)} />
          )}

          {showDetails && (
            <div>
              <h4 className="mb-2 text-sm font-semibold text-gray-900">More Details</h4>
              <div
                className="prose prose-sm max-w-none text-gray-700"
                dangerouslySetInnerHTML={{ __html: alert.moreDetails }}
              />
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
